<template>
  <section class="form-limited-width" v-if="localCourse">
    <h1 class="title mb-3">{{ $t('courseType.rehab') }}</h1>
    <base-form-group :label="$t('general.startDate')">
      <h5 class="form-text">{{ courseStartDate }}</h5>
    </base-form-group>
    <base-form-group
      :label="$t('general.therapist')"
      :errors="{
        required: !$v.localCourse.doctor_id.required
      }"
      :is-invalid="$v.localCourse.doctor_id.$error"
    >
      <h5 v-if="isOnlyDoctor" class="form-text">{{ getFullName(this.currentUser) }}</h5>
      <person-search-filter
        v-else
        :id="localCourse.doctor_id"
        @select:item="setDoctor"
        :find-by="findBy.doctor"
        :find-only-by-this="true"
      ></person-search-filter>
    </base-form-group>
    <base-form-group
      :label="$t('entity.patient')"
      :errors="{
        required: !$v.localCourse.patient_id.required,
        has406: has406
      }"
      :is-invalid="$v.localCourse.patient_id.$error || has406"
      :messages="{
        has406: $t('errorMessage.has406')
      }"
    >
      <person-search-filter
        v-show="!isAddPatientForm"
        :id="$v.localCourse.patient_id.$model"
        @select:item="setPatient"
        @openAddPatient="openAddPatient"
        @set:searchString="setSearchString"
        :find-by="findBy.patient"
        :find-only-by-this="true"
        :showAddPatient="true"
      ></person-search-filter>

      <button v-if="showAddPatientBtn" type="button" class="btn btn-primary mt-3" @click="openAddPatient">
        {{ $t('button.addNewPatient') }}
      </button>

      <div v-if="isAddPatientForm" class="card card-noshadow">
        <patient-form v-model="patient" @cancel="closeForm" @submit="save" :isCancelBtn="true"></patient-form>
      </div>
    </base-form-group>
    <button type="button" class="btn btn-primary" @click="submit" :disabled="isAddPatientForm">
      <span v-if="saving" class="spinner-border spinner-border-sm"></span>{{ $t('button.create') }}
    </button>
  </section>
</template>

<script>
import { fullName } from '@/mixins/full-name.js'
import { required } from 'vuelidate/lib/validators'
import { userRights } from '@/mixins/user-rights.js'
import { courseDate } from '@/mixins/course-date'
import { FIND_BY_PERSON } from '@/main.config'
import http from '@/modules/http.js'
import { systemUnavailable } from '@/mixins/system-unavailable.js'

export default {
  name: 'CourseAddForm',
  mixins: [fullName, userRights, courseDate, systemUnavailable],
  components: {
    PersonSearchFilter: () => import('@/components/Filters/PersonSearchFilter.vue'),
    BaseFormGroup: () => import('@/base/BaseFormGroup.vue'),
    PatientForm: () => import('@/components/PatientForm.vue')
  },
  model: {
    prop: 'course'
  },
  props: {
    course: {
      type: Object,
      required: true
    },
    serverError: {
      type: [Number],
      required: false
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    localCourse: null,
    isAddPatientForm: false,
    isShowAddPatientButton: true,
    patient: {}
  }),
  validations() {
    return {
      localCourse: {
        patient_id: {
          required
        },
        doctor_id: {
          required
        }
      }
    }
  },
  mounted() {
    this.localCourse = Object.assign({}, this.course)
  },
  watch: {
    localCourse: {
      deep: true,
      handler() {
        this.$emit('input', this.localCourse)
      }
    }
  },
  computed: {
    findBy() {
      return FIND_BY_PERSON
    },
    courseStartDate() {
      return this.getCourseFormattedStartDate(this.localCourse)
    },
    has406() {
      return this.serverError === 406
    },
    currentUser() {
      return this.$store.getters.AUTH_USER
    },
    showAddPatientBtn() {
      return !this.patient.id && this.isShowAddPatientButton
    }
  },
  methods: {
    submit() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.$emit('submit')
        this.$v.$reset()
      }
    },
    setDoctor(data) {
      this.localCourse.doctor_id = data.id
      this.$v.localCourse.doctor_id.$touch()
    },
    setPatient(data) {
      this.localCourse.patient_id = data.id
      this.$v.localCourse.patient_id.$touch()
      this.patient = { ...data.current }
      this.$emit('change')
    },
    openAddPatient() {
      this.isAddPatientForm = true
      this.isShowAddPatientButton = false
    },
    closeForm() {
      this.isAddPatientForm = false
      this.isShowAddPatientButton = true
    },
    setSearchString(stringName) {
      if (stringName == '') {
        this.localCourse.patient_id = null
        this.$v.localCourse.patient_id.$reset()
        this.$emit('change')
        this.patient = Object.assign({}, {})
      } else {
        let stringNameArray = stringName.split(' ')
        this.patient.surname = stringNameArray[0] ?? ''
        this.patient.name = stringNameArray[1] ?? ''
        this.patient.middle_name = stringNameArray[2] ?? ''
      }
    },
    save() {
      http
        .post('/patients', this.patient)
        .then(response => {
          if (response.data.id) {
            return response.data.id
          } else {
            throw this.$t('errorMessage.unexpectedError')
          }
        })
        .then(id => {
          if (id) {
            this.localCourse.patient_id = id
            this.patient.id = id
            this.isAddPatientForm = false
            this.isShowAddPatientButton = true
          }
        })
        .catch(error => {
          if (error.response.data.message === 'The system has expired') {
            this.$emit('system:unavailable')
          }
          console.log(error)
        })
    }
  }
}
</script>
